// src/hooks/useAuth.js
import { useState, useEffect, useCallback, useRef } from "react";
import {
  fbAuth, fbDb,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged,
  GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult,
  EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup,
  reauthenticateWithRedirect,
  deleteUser,
  doc, getDoc, setDoc, deleteDoc, updateDoc, writeBatch,
  collection, getDocs, query, where,
  serverTimestamp, deleteField,
} from "../services/firebase";

export const MAX_USERNAME_LENGTH = 30;
const ACCOUNT_DELETION_MARKER = "tripcanvas_account_deletion_uid";
const ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER = "tripcanvas_account_deletion_reauth_redirect_uid";
const PERSONAL_LOCAL_STORAGE_KEYS = ["lastLoginEmail", "tripcanvas_daily_cards"];
const PERSONAL_SESSION_STORAGE_KEYS = ["likedReviews", "likedTrips"];
const ANONYMOUS_TRAVELER_NAME = "탈퇴한 여행자";

const EMPTY_PERSONAL = {
  name: "", age: "", gender: "", bloodType: "",
  mbti: "", zodiac: "", address: "", email: "",
};

// 순서가 곧 실행 순서다. 되돌릴 수 없는 정도가 작은 것부터 간다.
// trips 이관은 '원본 삭제'까지 원자적으로 하므로 사용자에게서 완전히 사라진다.
// 반면 community·reviews 익명화는 콘텐츠 자체는 남는다(작성자 표시와 수정·삭제 권한만 잃는다).
// 그래서 trips를 비식별 단계의 **마지막**에 둔다 — 앞 단계에서 실패하면 저장 일정은 손대지 않은 채 멈춘다.
const createDeletionSteps = () => ([
  { key: "community", label: "커뮤니티 공유 일정 익명화", status: "pending", detail: "" },
  { key: "reviews", label: "작성한 후기 익명화", status: "pending", detail: "" },
  { key: "trips", label: "저장한 여행 일정 익명 이관", status: "pending", detail: "" },
  { key: "usernames", label: "닉네임 점유 해제", status: "pending", detail: "" },
  { key: "profile", label: "회원 프로필", status: "pending", detail: "" },
  { key: "local", label: "이 기기의 개인 저장 흔적", status: "pending", detail: "" },
  { key: "auth", label: "Firebase Auth 계정", status: "pending", detail: "" },
]);

const createDeletionState = () => ({
  status: "idle",
  provider: "",
  uid: "",
  message: "",
  steps: createDeletionSteps(),
});

const readDeletionMarker = () => {
  try {
    return localStorage.getItem(ACCOUNT_DELETION_MARKER)
      || sessionStorage.getItem(ACCOUNT_DELETION_MARKER)
      || "";
  } catch (e) {
    try { return sessionStorage.getItem(ACCOUNT_DELETION_MARKER) || ""; }
    catch (sessionError) { return ""; }
  }
};

const writeDeletionMarker = (uid) => {
  try { localStorage.setItem(ACCOUNT_DELETION_MARKER, uid); }
  catch (e) {}
  try { sessionStorage.setItem(ACCOUNT_DELETION_MARKER, uid); }
  catch (e) {}
};

const clearDeletionMarker = () => {
  try { localStorage.removeItem(ACCOUNT_DELETION_MARKER); }
  catch (e) {}
  try { sessionStorage.removeItem(ACCOUNT_DELETION_MARKER); }
  catch (e) {}
};

const readDeletionReauthRedirectMarker = () => {
  try {
    return localStorage.getItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER)
      || sessionStorage.getItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER)
      || "";
  } catch (e) {
    try { return sessionStorage.getItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER) || ""; }
    catch (sessionError) { return ""; }
  }
};

const writeDeletionReauthRedirectMarker = (uid) => {
  try { localStorage.setItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER, uid); }
  catch (e) {}
  try { sessionStorage.setItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER, uid); }
  catch (e) {}
};

const clearDeletionReauthRedirectMarker = () => {
  try { localStorage.removeItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER); }
  catch (e) {}
  try { sessionStorage.removeItem(ACCOUNT_DELETION_REAUTH_REDIRECT_MARKER); }
  catch (e) {}
};

const getDeletionProvider = (user) => (
  user?.providerData?.some(provider => provider.providerId === "google.com")
    ? "google"
    : "password"
);

const normalizeUsername = (value) => String(value ?? "")
  .normalize("NFKC")
  .trim()
  .replace(/\s+/g, " ")
  .toLowerCase();

const legacyUsernameKey = (value) => String(value ?? "").trim().toLowerCase();
const isPermissionDenied = (error) => ["permission-denied", "firestore/permission-denied"].includes(error?.code);

const profileError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

export function useAuth({ personal, setPersonal }) {
  const [authMode, setAuthMode] = useState(null); // null | "signup" | "login" | "profile"
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [firebaseUid, setFirebaseUid] = useState(null);
  const [loginEmail, setLoginEmail] = useState(() => {
    try { return localStorage.getItem("lastLoginEmail") || ""; } catch (e) { return ""; }
  });
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState("");
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showSignupPw, setShowSignupPw] = useState(false);
  const [rememberPw, setRememberPw] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [nameCheckStatus, setNameCheckStatus] = useState("idle");
  const [nameCheckMsg, setNameCheckMsg] = useState("");
  const [privacyAgreed, setPrivacyAgreedState] = useState(false);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);
  const [overseasTransferAgreed, setOverseasTransferAgreedState] = useState(false);
  const [overseasTransferExpanded, setOverseasTransferExpanded] = useState(false);
  const privacyAgreedRef = useRef(false);
  const overseasTransferAgreedRef = useRef(false);
  const persistedPrivacyAgreedRef = useRef(false);
  const persistedOverseasTransferAgreedRef = useRef(false);
  const consentActionRef = useRef(null);
  const [consentGate, setConsentGate] = useState({
    open: false,
    actionLabel: "",
    saving: false,
    error: "",
  });
  const [accountDeletion, setAccountDeletion] = useState(createDeletionState);
  // 탈퇴 도중 이미 되돌릴 수 없게 된 것을 센다. 실패 안내에 사실대로 적기 위해서다.
  // 단계가 통째로 실패해도 그 안에서 일부는 이미 처리됐을 수 있어, 단계 성공 여부와 따로 센다.
  const irreversibleProgressRef = useRef({ anonymized: 0, archived: 0 });
  // Google 재인증 redirect에서 돌아와 탈퇴가 자동 재개됐는지. 화면을 그쪽으로 데려가는 데 쓴다.
  const [deletionResumedFromRedirect, setDeletionResumedFromRedirect] = useState(false);
  const [profileRequired, setProfileRequired] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileErrorMessage, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState("");
  const profileRequestRef = useRef(0);

  const setPrivacyAgreed = useCallback((nextValue) => {
    const next = typeof nextValue === "function"
      ? nextValue(privacyAgreedRef.current)
      : nextValue;
    privacyAgreedRef.current = next === true;
    setPrivacyAgreedState(next === true);
  }, []);

  const setOverseasTransferAgreed = useCallback((nextValue) => {
    const next = typeof nextValue === "function"
      ? nextValue(overseasTransferAgreedRef.current)
      : nextValue;
    overseasTransferAgreedRef.current = next === true;
    setOverseasTransferAgreedState(next === true);
  }, []);

  const rememberAuthenticatedUser = useCallback((user) => {
    const email = user?.email || "";
    setFirebaseUid(user?.uid || null);
    setLoggedInUser(email || null);
    setLoginEmail(email);
    try { localStorage.setItem("lastLoginEmail", email); } catch (e) {}
    return email;
  }, []);

  // 구 규칙에서 인덱스 접근이 막혔던 계정과 NFKC 도입 전 키를 로그인 때 보수한다.
  const repairUsernameIndex = useCallback(async (uid, displayName) => {
    const normalizedName = normalizeUsername(displayName);
    if (!uid || !normalizedName || normalizedName.includes("/")) return;

    try {
      const normalizedRef = doc(fbDb, "usernames", normalizedName);
      const normalizedSnap = await getDoc(normalizedRef);
      if (normalizedSnap.exists() && normalizedSnap.data()?.uid !== uid) {
        console.warn("[username-index] 정규화된 닉네임이 다른 사용자 소유라 자동 보수를 건너뜁니다.", { uid, normalizedName });
        return;
      }

      const legacyName = legacyUsernameKey(displayName);
      let ownedLegacyRef = null;
      if (legacyName && legacyName !== normalizedName && !legacyName.includes("/")) {
        const legacyRef = doc(fbDb, "usernames", legacyName);
        const legacySnap = await getDoc(legacyRef);
        if (legacySnap.exists() && legacySnap.data()?.uid === uid) ownedLegacyRef = legacyRef;
      }

      if (!normalizedSnap.exists() || ownedLegacyRef) {
        const batch = writeBatch(fbDb);
        batch.set(normalizedRef, { uid });
        if (ownedLegacyRef) batch.delete(ownedLegacyRef);
        await batch.commit();
      }
    } catch (error) {
      console.warn("[username-index] 로그인 후 인덱스 자동 보수를 완료하지 못했습니다.", error);
    }
  }, []);

  // 이메일/Google/redirect가 모두 공유하는 프로필 판별 경로.
  const handleAuthenticatedUser = useCallback(async (user) => {
    if (!user) return null;
    const requestId = ++profileRequestRef.current;
    const email = rememberAuthenticatedUser(user);
    setProfileLoading(true);
    setProfileLoadError("");

    try {
      const profileSnap = await getDoc(doc(fbDb, "users", user.uid));
      if (requestId !== profileRequestRef.current) return null;

      if (profileSnap.exists()) {
        const storedData = profileSnap.data() || {};
        const storedProfile = storedData.profile && typeof storedData.profile === "object" && !Array.isArray(storedData.profile)
          ? storedData.profile
          : {};
        setPersonal({
          ...EMPTY_PERSONAL,
          ...storedProfile,
          email: storedData.email || storedProfile.email || email,
        });
        persistedPrivacyAgreedRef.current = storedData.privacyAgreed === true;
        persistedOverseasTransferAgreedRef.current = storedData.overseasTransferAgreed === true;
        setPrivacyAgreed(storedData.privacyAgreed === true);
        setOverseasTransferAgreed(storedData.overseasTransferAgreed === true);
        setProfileRequired(false);
        setAuthMode(null);
        setNameCheckStatus("idle");
        setNameCheckMsg("");
        const accountDeletionPending = readDeletionMarker() === user.uid
          || readDeletionReauthRedirectMarker() === user.uid;
        if (accountDeletionPending) {
          setAccountDeletion(current => current.status === "idle"
            ? {
                ...createDeletionState(),
                status: "error",
                provider: user.providerData?.some(provider => provider.providerId === "google.com") ? "google" : "password",
                uid: user.uid,
                message: "이전에 시작한 회원 탈퇴가 완료되지 않았습니다. Google 복귀 처리를 기다리거나 내 정보에서 다시 시도해주세요.",
              }
            : current);
        }
        if (!accountDeletionPending) repairUsernameIndex(user.uid, storedProfile.name);
        return true;
      }

      const isGoogleUser = user.providerData?.some(provider => provider.providerId === "google.com");
      setPersonal(current => isGoogleUser
        ? { ...EMPTY_PERSONAL, name: user.displayName?.trim() || "", email }
        : { ...EMPTY_PERSONAL, ...current, email });
      persistedPrivacyAgreedRef.current = false;
      persistedOverseasTransferAgreedRef.current = false;
      setPrivacyAgreed(false);
      setOverseasTransferAgreed(false);
      if (readDeletionMarker() === user.uid || readDeletionReauthRedirectMarker() === user.uid) {
        setProfileRequired(false);
        setAuthMode(null);
        setAccountDeletion({
          ...createDeletionState(),
          status: "error",
          provider: isGoogleUser ? "google" : "password",
          uid: user.uid,
          message: "회원 데이터 삭제 후 계정 삭제가 중단되었습니다. 내 정보의 회원 탈퇴에서 재인증하고 다시 시도해주세요.",
        });
        return false;
      }
      setProfileRequired(true);
      setAuthMode("profile");
      setNameCheckStatus("idle");
      setNameCheckMsg("");
      return false;
    } catch (error) {
      console.error("프로필 로드 오류:", error);
      if (requestId === profileRequestRef.current) {
        persistedPrivacyAgreedRef.current = false;
        persistedOverseasTransferAgreedRef.current = false;
        setPrivacyAgreed(false);
        setOverseasTransferAgreed(false);
        setProfileLoadError("프로필 정보를 불러오지 못했습니다. 네트워크 연결을 확인한 뒤 다시 로그인해주세요.");
      }
      return null;
    } finally {
      if (requestId === profileRequestRef.current) setProfileLoading(false);
    }
  }, [rememberAuthenticatedUser, repairUsernameIndex, setOverseasTransferAgreed, setPersonal, setPrivacyAgreed]);

  // ── Google 인증 결과 공통 처리 ──
  const handleGoogleAuthResult = useCallback(async (user) => {
    if (!user) return null;
    return handleAuthenticatedUser(user);
  }, [handleAuthenticatedUser]);

  const showGoogleError = useCallback((error) => {
    const code = error.code || "";
    let msg = "";
    if (code === "auth/operation-not-allowed") {
      msg = "Firebase Console > Authentication > Sign-in method 에서 Google 로그인을 활성화해주세요.";
    } else if (code === "auth/unauthorized-domain") {
      msg = "Firebase Console > Authentication > Settings > Authorized domains 에 현재 도메인을 추가해주세요.";
    } else if (code === "auth/account-exists-with-different-credential") {
      msg = "이메일이 이미 다른 방법으로 가입되어 있어요. 이메일+비밀번호로 로그인해주세요.";
    } else if (code === "auth/network-request-failed") {
      msg = "네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.";
    } else {
      msg = "Google 로그인 실패: " + (error.message || code || "다시 시도해주세요");
    }
    setLoginError(msg);
    console.error("[Google Sign-in]", code, error);
  }, []);

  // ── Firebase Auth 상태 감지 + 로그인 사용자 프로필 복원 ──
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(fbAuth, (fbUser) => {
      if (fbUser) {
        handleAuthenticatedUser(fbUser);
        return;
      }

      ++profileRequestRef.current;
      setFirebaseUid(null);
      setLoggedInUser(null);
      setProfileRequired(false);
      setProfileLoading(false);
      setProfileLoadError("");
      persistedPrivacyAgreedRef.current = false;
      persistedOverseasTransferAgreedRef.current = false;
      setPrivacyAgreed(false);
      setOverseasTransferAgreed(false);
      setPersonal({ ...EMPTY_PERSONAL });
    });
    return unsubscribe;
  }, [handleAuthenticatedUser, setOverseasTransferAgreed, setPersonal, setPrivacyAgreed]);

  // ── [Firebase] 닉네임 중복확인 ──
  const checkNameDuplicate = async (targetName) => {
    const normalizedName = normalizeUsername(targetName);
    if (!normalizedName) {
      setNameCheckMsg("닉네임을 먼저 입력해주세요.");
      setNameCheckStatus("fail");
      return "fail";
    }
    if (normalizedName.includes("/")) {
      setNameCheckMsg("닉네임에는 / 문자를 사용할 수 없습니다.");
      setNameCheckStatus("fail");
      return "fail";
    }
    if (normalizedName.length > MAX_USERNAME_LENGTH) {
      setNameCheckMsg(`닉네임은 ${MAX_USERNAME_LENGTH}자 이하로 입력해주세요.`);
      setNameCheckStatus("fail");
      return "fail";
    }

    setNameCheckStatus("checking");
    try {
      const usernameSnap = await getDoc(doc(fbDb, "usernames", normalizedName));
      const myUid = firebaseUid || fbAuth.currentUser?.uid;
      const ownerUid = usernameSnap.exists() ? usernameSnap.data()?.uid : null;
      if (usernameSnap.exists() && ownerUid !== myUid) {
        setNameCheckStatus("fail");
        setNameCheckMsg("이미 사용 중인 닉네임입니다.");
        return "fail";
      }
      setNameCheckStatus("ok");
      setNameCheckMsg("사용 가능한 닉네임입니다. ✓");
      return "ok";
    } catch (error) {
      console.warn("[username-check] 닉네임 중복확인을 완료하지 못했습니다. 가입은 계속 허용합니다.", error);
      setNameCheckStatus("unavailable");
      setNameCheckMsg("지금은 중복 확인을 할 수 없어요. 가입은 계속할 수 있습니다.");
      return "unavailable";
    }
  };

  const persistProfile = async ({ uid, email, profileData, isNewAccount = false }) => {
    const normalizedName = normalizeUsername(profileData?.name);
    if (!normalizedName || normalizedName.includes("/")) {
      throw profileError("profile/invalid-username", "유효하지 않은 닉네임입니다.");
    }
    if (normalizedName.length > MAX_USERNAME_LENGTH) {
      throw profileError("profile/username-too-long", `닉네임은 ${MAX_USERNAME_LENGTH}자 이하로 입력해주세요.`);
    }
    if (!privacyAgreedRef.current || !overseasTransferAgreedRef.current) {
      throw profileError("profile/consents-required", "두 가지 필수 개인정보 동의가 모두 필요합니다.");
    }

    const userRef = doc(fbDb, "users", uid);
    const usernameRef = doc(fbDb, "usernames", normalizedName);
    let usernameReadUnavailable = false;
    let usernameSnap = null;

    // 이 조회는 "규칙 미배포(환경 문제)"와 "이미 선점됨"을 구분하는 마지막 관문이다.
    // 둘 다 permission-denied로 오기 때문에, 일시적 실패를 환경 문제로 오인하면
    // 남이 쓰고 있는 닉네임을 그대로 저장해 버린다.
    // → 한 번 더 조회해 '진짜로 못 읽는 상태'인지 확인한 뒤에만 호환 경로로 넘어간다.
    try {
      usernameSnap = await getDoc(usernameRef);
    } catch (firstError) {
      try {
        await new Promise(resolve => setTimeout(resolve, 400));
        usernameSnap = await getDoc(usernameRef);
        console.warn("[username-index] 첫 조회는 실패했으나 재조회에 성공했습니다.", firstError);
      } catch (retryError) {
        usernameReadUnavailable = true;
        console.warn("[username-index] 재조회까지 실패해 배포 호환 경로로 진행합니다. 배포된 규칙을 확인해주세요.", retryError);
      }
    }

    if (usernameSnap?.exists() && usernameSnap.data()?.uid !== uid) {
      throw profileError("profile/username-taken", "이미 사용 중인 닉네임입니다.");
    }

    const existingUserSnap = await getDoc(userRef);
    const existingData = existingUserSnap.exists() ? existingUserSnap.data() : {};
    const oldName = existingData?.profile?.name;
    const oldNormalizedName = normalizeUsername(oldName);
    let ownedOldUsernameRef = null;

    if (oldNormalizedName && oldNormalizedName !== normalizedName && !oldNormalizedName.includes("/")) {
      try {
        const oldRef = doc(fbDb, "usernames", oldNormalizedName);
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists() && oldSnap.data()?.uid === uid) ownedOldUsernameRef = oldRef;
      } catch (error) {
        console.warn("[username-index] 옛 닉네임 소유권을 확인하지 못해 삭제하지 않습니다.", error);
      }
    }

    const now = Date.now();
    const userPayload = {
      email,
      profile: { ...profileData, email },
      privacyAgreed: true,
      privacyAgreedAt: existingData.privacyAgreed === true && existingData.privacyAgreedAt
        ? existingData.privacyAgreedAt
        : now,
      overseasTransferAgreed: true,
      overseasTransferAgreedAt: existingData.overseasTransferAgreed === true && existingData.overseasTransferAgreedAt
        ? existingData.overseasTransferAgreedAt
        : now,
      updatedAt: now,
    };
    if (isNewAccount || !existingUserSnap.exists()) userPayload.createdAt = existingData.createdAt || now;

    const batch = writeBatch(fbDb);
    batch.set(usernameRef, { uid });
    if (ownedOldUsernameRef) batch.delete(ownedOldUsernameRef);
    batch.set(userRef, userPayload, { merge: true });

    try {
      await batch.commit();
      return { indexDeferred: false };
    } catch (error) {
      // 앱을 먼저 배포했을 때 구 규칙은 users 쓰기만 허용하고 usernames는 거부한다.
      if (usernameReadUnavailable && isPermissionDenied(error)) {
        let retrySnap = null;
        try {
          await new Promise(resolve => setTimeout(resolve, 150));
          retrySnap = await getDoc(usernameRef);
        } catch (retryError) {
          if (!isPermissionDenied(retryError)) throw retryError;
          console.warn("[username-index] 저장 직전 재확인도 권한 거부되어 구 규칙 호환 모드로 진행합니다.", retryError);
        }
        if (retrySnap?.exists() && retrySnap.data()?.uid !== uid) {
          throw profileError("profile/username-taken", "이미 사용 중인 닉네임입니다.");
        }
        console.warn("[username-index] 구 Firestore 규칙 호환 모드: 프로필을 저장하고 인덱스 보수를 다음 로그인으로 미룹니다.", error);
        await setDoc(userRef, userPayload, { merge: true });
        return { indexDeferred: true };
      }
      throw error;
    }
  };

  // ── [Firebase] Auth: 회원가입 ──
  const saveUserAccount = async (email, password, profileData) => {
    setProfileError("");
    const normalizedName = normalizeUsername(profileData?.name);
    if (!normalizedName || normalizedName.includes("/")) {
      throw profileError("profile/invalid-username", "유효하지 않은 닉네임입니다.");
    }
    if (normalizedName.length > MAX_USERNAME_LENGTH) {
      throw profileError("profile/username-too-long", `닉네임은 ${MAX_USERNAME_LENGTH}자 이하로 입력해주세요.`);
    }
    if (!privacyAgreedRef.current || !overseasTransferAgreedRef.current) {
      throw profileError("profile/consents-required", "두 가지 필수 개인정보 동의가 모두 필요합니다.");
    }

    const cred = await createUserWithEmailAndPassword(fbAuth, email, password);
    const uid = cred.user.uid;
    rememberAuthenticatedUser(cred.user);
    setProfileRequired(true);

    try {
      await persistProfile({ uid, email, profileData, isNewAccount: true });
      persistedPrivacyAgreedRef.current = true;
      persistedOverseasTransferAgreedRef.current = true;
      setPrivacyAgreed(true);
      setOverseasTransferAgreed(true);
      setProfileRequired(false);
      setNameCheckStatus("ok");
      return true;
    } catch (error) {
      console.error("Firestore 닉네임 인덱스 또는 프로필 저장 실패 (Auth는 성공):", error);
      setAuthMode("profile");
      setProfileRequired(true);
      if (error?.code === "profile/username-taken") {
        setNameCheckStatus("fail");
        setNameCheckMsg("이미 사용 중인 닉네임입니다. 다른 닉네임을 선택해주세요.");
      }
      const recoveryError = profileError(
        "profile/account-created-needs-completion",
        "계정은 생성되었습니다. 이 화면에서 프로필 저장을 다시 시도해주세요."
      );
      recoveryError.cause = error;
      setProfileError(recoveryError.message);
      throw recoveryError;
    }
  };

  // ── [Firebase] Auth: 로그인 ──
  const loginUser = async () => {
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError("이메일과 비밀번호를 입력해주세요.");
      return;
    }
    try {
      const cred = await signInWithEmailAndPassword(fbAuth, loginEmail.trim(), loginPassword);
      const hasProfile = await handleAuthenticatedUser(cred.user);
      setLoginPassword("");
      setLoginError("");
      if (hasProfile === true) setAuthMode(null);
    } catch (error) {
      const code = error.code || "";
      if (code.includes("user-not-found") || code.includes("invalid-credential")) {
        setLoginError("등록된 이메일이 없거나 비밀번호가 올바르지 않습니다.");
      } else if (code.includes("too-many-requests")) {
        setLoginError("로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.");
      } else {
        setLoginError("로그인 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    }
  };

  const googleSignIn = async () => {
    if (googleLoading) return;
    setLoginError("");
    setGoogleLoading(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      try {
        await signInWithRedirect(fbAuth, provider);
      } catch (error) {
        setGoogleLoading(false);
        showGoogleError(error);
      }
      return;
    }

    try {
      const result = await signInWithPopup(fbAuth, provider);
      await handleGoogleAuthResult(result.user);
      setGoogleLoading(false);
    } catch (error) {
      const code = error.code || "";
      if (code === "auth/popup-blocked" || code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        if (code === "auth/popup-closed-by-user") {
          setGoogleLoading(false);
          return;
        }
        try {
          await signInWithRedirect(fbAuth, provider);
        } catch (redirectError) {
          setGoogleLoading(false);
          showGoogleError(redirectError);
        }
        return;
      }
      setGoogleLoading(false);
      showGoogleError(error);
    }
  };

  const hasRequiredConsents = useCallback(() => (
    fbAuth.currentUser
      ? persistedPrivacyAgreedRef.current && persistedOverseasTransferAgreedRef.current
      : privacyAgreedRef.current && overseasTransferAgreedRef.current
  ), []);

  const requestRequiredConsent = useCallback(async (actionLabel, onContinue) => {
    const consentIsEffective = fbAuth.currentUser
      ? persistedPrivacyAgreedRef.current && persistedOverseasTransferAgreedRef.current
      : privacyAgreedRef.current && overseasTransferAgreedRef.current;
    if (consentIsEffective) {
      return onContinue ? onContinue() : true;
    }
    consentActionRef.current = typeof onContinue === "function" ? onContinue : null;
    setConsentGate({
      open: true,
      actionLabel: actionLabel || "이 기능",
      saving: false,
      error: "",
    });
    return false;
  }, []);

  const closeConsentGate = useCallback(() => {
    consentActionRef.current = null;
    setConsentGate({ open: false, actionLabel: "", saving: false, error: "" });
  }, []);

  const confirmConsentGate = useCallback(async (draftConsents = {}) => {
    const privacyAccepted = draftConsents.privacyAgreed === true;
    const overseasAccepted = draftConsents.overseasTransferAgreed === true;
    if (!privacyAccepted || !overseasAccepted) {
      setConsentGate(current => ({
        ...current,
        error: "수집·이용 동의와 국외 이전 동의에 모두 체크해주세요.",
      }));
      return false;
    }

    setConsentGate(current => ({ ...current, saving: true, error: "" }));
    try {
      const user = fbAuth.currentUser;
      if (user) {
        const userRef = doc(fbDb, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const existingData = userSnap.exists() ? userSnap.data() : {};
        const now = Date.now();
        await setDoc(userRef, {
          privacyAgreed: true,
          privacyAgreedAt: existingData.privacyAgreed === true && existingData.privacyAgreedAt
            ? existingData.privacyAgreedAt
            : now,
          overseasTransferAgreed: true,
          overseasTransferAgreedAt: existingData.overseasTransferAgreed === true && existingData.overseasTransferAgreedAt
            ? existingData.overseasTransferAgreedAt
            : now,
          updatedAt: now,
        }, { merge: true });
      }

      persistedPrivacyAgreedRef.current = true;
      persistedOverseasTransferAgreedRef.current = true;
      setPrivacyAgreed(true);
      setOverseasTransferAgreed(true);
      const action = consentActionRef.current;
      consentActionRef.current = null;
      setConsentGate({ open: false, actionLabel: "", saving: false, error: "" });
      if (action) await action();
      return true;
    } catch (error) {
      console.error("[privacy-consent] 동의 저장 실패:", error);
      setConsentGate(current => ({
        ...current,
        saving: false,
        error: "동의 내용을 저장하지 못했습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.",
      }));
      return false;
    }
  }, [setOverseasTransferAgreed, setPrivacyAgreed]);

  const updateDeletionStep = useCallback((key, patch) => {
    setAccountDeletion(current => ({
      ...current,
      steps: current.steps.map(step => step.key === key ? { ...step, ...patch } : step),
    }));
  }, []);

  const skipDeletionSteps = useCallback((keys, detail) => {
    for (const key of keys) {
      updateDeletionStep(key, { status: "skipped", detail });
    }
  }, [updateDeletionStep]);

  const deleteOwnedDocuments = useCallback(async (key, targetQuery) => {
    updateDeletionStep(key, { status: "running", detail: "삭제 대상을 확인하는 중..." });
    try {
      const snapshot = await getDocs(targetQuery);
      let deletedCount = 0;
      const failures = [];
      for (const targetDoc of snapshot.docs) {
        try {
          await deleteDoc(targetDoc.ref);
          deletedCount += 1;
        } catch (error) {
          try {
            const verificationSnap = await getDoc(targetDoc.ref);
            if (!verificationSnap.exists()) {
              deletedCount += 1;
            } else {
              failures.push({ error, statusUnknown: false });
            }
          } catch (verificationError) {
            failures.push({ error, verificationError, statusUnknown: true });
          }
        }
      }

      if (failures.length > 0) {
        const remainingCount = failures.filter(failure => !failure.statusUnknown).length;
        const unknownCount = failures.length - remainingCount;
        const remainingDetail = [
          `${deletedCount}개 삭제`,
          remainingCount > 0 ? `${remainingCount}개 남음` : "",
          unknownCount > 0 ? `${unknownCount}개 상태 확인 불가` : "",
        ].filter(Boolean).join(", ");
        updateDeletionStep(key, {
          status: "failed",
          detail: remainingDetail,
        });
        return false;
      }

      updateDeletionStep(key, {
        status: "done",
        detail: snapshot.size > 0 ? `${deletedCount}개 삭제 완료` : "남은 식별 문서 없음(이미 완료)",
      });
      return true;
    } catch (error) {
      console.error(`[account-deletion] ${key} 삭제 실패:`, error);
      updateDeletionStep(key, {
        status: "failed",
        detail: "삭제 대상을 조회하지 못해 남은 수를 확인할 수 없음",
      });
      return false;
    }
  }, [updateDeletionStep]);

  const anonymizeOwnedDocuments = useCallback(async (key, targetQuery, ownershipField, anonymizedFields) => {
    updateDeletionStep(key, { status: "running", detail: "개인 식별 정보가 남은 문서를 확인하는 중..." });
    try {
      const snapshot = await getDocs(targetQuery);
      let anonymizedCount = 0;
      const failures = [];
      for (const targetDoc of snapshot.docs) {
        try {
          await updateDoc(targetDoc.ref, anonymizedFields);
          anonymizedCount += 1;
        } catch (error) {
          console.error(`[account-deletion] ${key} 문서 ${targetDoc.id} 익명화 실패:`, error);
          try {
            const verificationSnap = await getDoc(targetDoc.ref);
            if (!verificationSnap.exists() || verificationSnap.data()?.[ownershipField] == null) {
              anonymizedCount += 1;
            } else {
              failures.push({ error, statusUnknown: false });
            }
          } catch (verificationError) {
            failures.push({ error, verificationError, statusUnknown: true });
          }
        }
      }

      irreversibleProgressRef.current.anonymized += anonymizedCount;

      if (failures.length > 0) {
        const remainingCount = failures.filter(failure => !failure.statusUnknown).length;
        const unknownCount = failures.length - remainingCount;
        const failureDetail = [
          `${anonymizedCount}개 익명화`,
          remainingCount > 0 ? `${remainingCount}개 식별 문서 남음` : "",
          unknownCount > 0 ? `${unknownCount}개 상태 확인 불가` : "",
        ].filter(Boolean).join(", ");
        updateDeletionStep(key, {
          status: "failed",
          detail: failureDetail,
        });
        return false;
      }

      updateDeletionStep(key, {
        status: "done",
        detail: snapshot.size > 0
          ? `${anonymizedCount}개 익명화 완료·콘텐츠 보존`
          : "남은 식별 문서 없음(이미 완료)",
      });
      return true;
    } catch (error) {
      console.error(`[account-deletion] ${key} 익명화 대상 조회 실패:`, error);
      updateDeletionStep(key, {
        status: "failed",
        detail: "익명화 대상을 조회하지 못해 남은 수를 확인할 수 없음",
      });
      return false;
    }
  }, [updateDeletionStep]);

  const archiveOwnedTrips = useCallback(async (uid) => {
    updateDeletionStep("trips", { status: "running", detail: "익명 이관할 일정을 확인하는 중..." });
    try {
      const snapshot = await getDocs(collection(fbDb, "users", uid, "trips"));
      let archivedCount = 0;
      const failures = [];

      for (const tripDoc of snapshot.docs) {
        try {
          const trip = tripDoc.data() || {};
          // 문서 ID는 Firestore가 만드는 무작위 값을 쓴다.
          // 예전엔 SHA-256(uid+tripId)로 파생시켰으나, tripId가 Date.now() 밀리초라
          // 엔트로피가 낮아 uid를 아는 사람이 역추적할 수 있었다(실측으로 반증됨).
          // 아래 배치가 '아카이브 생성 + 원본 삭제'를 원자적으로 처리하므로
          // 멱등성을 위해 ID를 파생시킬 이유가 없다 — 커밋되면 원본이 사라져 재시도 대상에서 빠진다.
          const archiveRef = doc(collection(fbDb, "archivedTrips"));
          const batch = writeBatch(fbDb);
          batch.set(archiveRef, {
            destination: trip.destination ?? "",
            startDate: trip.startDate ?? "",
            endDate: trip.endDate ?? "",
            itinerary: trip.itinerary ?? "",
            spots: Array.isArray(trip.spots) ? trip.spots : [],
            archivedAt: serverTimestamp(),
          });
          batch.delete(tripDoc.ref);
          await batch.commit();
          archivedCount += 1;
        } catch (error) {
          console.error(`[account-deletion] 여행 ${tripDoc.id} 익명 이관 실패:`, error);
          try {
            const verificationSnap = await getDoc(tripDoc.ref);
            if (!verificationSnap.exists()) {
              archivedCount += 1;
            } else {
              failures.push({ error, statusUnknown: false });
            }
          } catch (verificationError) {
            failures.push({ error, verificationError, statusUnknown: true });
          }
        }
      }

      irreversibleProgressRef.current.archived += archivedCount;

      if (failures.length > 0) {
        const remainingCount = failures.filter(failure => !failure.statusUnknown).length;
        const unknownCount = failures.length - remainingCount;
        const failureDetail = [
          `${archivedCount}개 이관`,
          remainingCount > 0 ? `${remainingCount}개 원본 일정 남음` : "",
          unknownCount > 0 ? `${unknownCount}개 상태 확인 불가` : "",
        ].filter(Boolean).join(", ");
        updateDeletionStep("trips", {
          status: "failed",
          detail: failureDetail,
        });
        return false;
      }

      updateDeletionStep("trips", {
        status: "done",
        detail: snapshot.size > 0
          ? `${archivedCount}개 익명 이관 완료·개인 경로 원본 삭제`
          : "이관할 원본 없음(이미 완료)",
      });
      return true;
    } catch (error) {
      console.error("[account-deletion] 저장 일정 조회 실패:", error);
      updateDeletionStep("trips", {
        status: "failed",
        detail: "저장 일정을 조회하지 못해 남은 수를 확인할 수 없음",
      });
      return false;
    }
  }, [updateDeletionStep]);

  const clearLocalPersonalData = useCallback(() => {
    const failedKeys = [];
    for (const key of PERSONAL_LOCAL_STORAGE_KEYS) {
      try { localStorage.removeItem(key); }
      catch (error) { failedKeys.push(`localStorage:${key}`); }
    }
    for (const key of PERSONAL_SESSION_STORAGE_KEYS) {
      try { sessionStorage.removeItem(key); }
      catch (error) { failedKeys.push(`sessionStorage:${key}`); }
    }
    return failedKeys;
  }, []);

  const runAccountDeletion = useCallback(async (user, provider) => {
    setAccountDeletion({
      ...createDeletionState(),
      status: "deleting",
      provider,
      uid: user.uid,
      message: "공유 콘텐츠를 익명화하고 저장 일정을 이관하고 있습니다. 이 화면을 닫지 마세요.",
    });
    writeDeletionMarker(user.uid);
    irreversibleProgressRef.current = { anonymized: 0, archived: 0 };

    // 실행 순서 = createDeletionSteps 순서. trips 이관(원본 삭제까지 원자적)이 마지막이다.
    const preProfileStages = [
      {
        key: "community",
        run: () => anonymizeOwnedDocuments(
          "community",
          query(collection(fbDb, "community"), where("uid", "==", user.uid)),
          "uid",
          {
            uid: deleteField(),
            name: ANONYMOUS_TRAVELER_NAME,
            mbti: deleteField(),
            bloodType: deleteField(),
          }
        ),
      },
      {
        key: "reviews",
        run: () => anonymizeOwnedDocuments(
          "reviews",
          query(collection(fbDb, "reviews"), where("authorUid", "==", user.uid)),
          "authorUid",
          {
            authorUid: deleteField(),
            author: ANONYMOUS_TRAVELER_NAME,
          }
        ),
      },
      {
        key: "trips",
        run: () => archiveOwnedTrips(user.uid),
      },
      {
        key: "usernames",
        run: () => deleteOwnedDocuments(
          "usernames",
          query(collection(fbDb, "usernames"), where("uid", "==", user.uid))
        ),
      },
    ];

    for (let stageIndex = 0; stageIndex < preProfileStages.length; stageIndex += 1) {
      const stage = preProfileStages[stageIndex];
      const completed = await stage.run();
      if (!completed) {
        skipDeletionSteps(
          preProfileStages.slice(stageIndex + 1).map(nextStage => nextStage.key),
          "이전 단계 실패로 보류"
        );
        skipDeletionSteps(
          ["profile", "local", "auth"],
          "식별 정보가 남을 수 있어 안전을 위해 보류"
        );
        // 계정은 살아남지만 앞 단계에서 이미 되돌릴 수 없게 된 것이 있을 수 있다.
        // "계정과 프로필은 유지됩니다"만 알리면 사용자는 아무것도 잃지 않았다고 읽는다.
        const { anonymized, archived } = irreversibleProgressRef.current;
        const irreversibleNotes = [
          anonymized > 0
            ? `이미 익명 처리된 공유 일정·후기 ${anonymized}건은 작성자 표시가 사라져 앱에서 수정·삭제할 수 없습니다.`
            : "",
          archived > 0
            ? `이미 익명 보존소로 옮겨진 저장 일정 ${archived}건은 보관함에서 사라지며 앱에서 다시 볼 수 없습니다.`
            : "",
        ].filter(Boolean).join(" ");
        setAccountDeletion(current => ({
          ...current,
          status: "error",
          message: [
            "일부 익명화·이관을 완료하지 못했습니다. 계정과 프로필은 유지됩니다.",
            irreversibleNotes,
            "표시된 남은 항목을 확인한 뒤 다시 시도해주세요.",
          ].filter(Boolean).join(" "),
        }));
        return false;
      }
    }

    updateDeletionStep("profile", { status: "running", detail: "회원 문서를 삭제하는 중..." });
    try {
      await deleteDoc(doc(fbDb, "users", user.uid));
      updateDeletionStep("profile", { status: "done", detail: "삭제 완료" });
    } catch (error) {
      console.error("[account-deletion] 회원 프로필 삭제 실패:", error);
      updateDeletionStep("profile", { status: "failed", detail: "회원 문서가 남아 있음" });
      updateDeletionStep("local", { status: "skipped", detail: "탈퇴 재시도를 위해 보류" });
      updateDeletionStep("auth", {
        status: "skipped",
        detail: "삭제 권한을 보존하기 위해 계정을 유지함",
      });
      setAccountDeletion(current => ({
        ...current,
        status: "error",
        message: "회원 프로필을 삭제하지 못했습니다. Auth 계정은 유지했으므로 다시 시도할 수 있습니다.",
      }));
      return false;
    }

    const failedStorageKeys = clearLocalPersonalData();
    updateDeletionStep("local", failedStorageKeys.length > 0
      ? { status: "failed", detail: `삭제하지 못한 키 ${failedStorageKeys.length}개` }
      : { status: "done", detail: "로그인 이메일·좋아요·개인 카드 기록 삭제 완료" });

    updateDeletionStep("auth", { status: "running", detail: "인증 계정을 삭제하는 중..." });
    try {
      await deleteUser(user);
      updateDeletionStep("auth", { status: "done", detail: "삭제 완료" });
    } catch (error) {
      console.error("[account-deletion] Firebase Auth 계정 삭제 실패:", error);
      const requiresRecentLogin = error?.code === "auth/requires-recent-login";
      updateDeletionStep("auth", {
        status: "failed",
        detail: requiresRecentLogin ? "최근 로그인 재인증 필요" : "Auth 계정이 남아 있음",
      });
      setAccountDeletion(current => ({
        ...current,
        status: requiresRecentLogin ? "requiresReauth" : "error",
        message: requiresRecentLogin
          ? "익명화·이관과 프로필 삭제는 완료됐지만 Auth 계정이 남았습니다. 다시 본인 확인하면 완료된 단계는 건너뛰고 계정 삭제를 이어갑니다."
          : "익명화·이관과 프로필 삭제는 완료됐지만 Auth 계정 삭제에 실패했습니다. 다시 시도해주세요.",
      }));
      return false;
    }

    clearDeletionMarker();
    clearDeletionReauthRedirectMarker();
    ++profileRequestRef.current;
    setLoggedInUser(null);
    setFirebaseUid(null);
    setAuthMode(null);
    setLoginEmail("");
    setLoginPassword("");
    setSignupPassword("");
    setSignupPasswordConfirm("");
    setNameCheckStatus("idle");
    setNameCheckMsg("");
    persistedPrivacyAgreedRef.current = false;
    persistedOverseasTransferAgreedRef.current = false;
    setPrivacyAgreed(false);
    setOverseasTransferAgreed(false);
    setProfileRequired(false);
    setProfileError("");
    setProfileLoadError("");
    setPersonal({ ...EMPTY_PERSONAL });
    setAccountDeletion(current => ({
      ...current,
      status: "complete",
      message: failedStorageKeys.length > 0
        ? "계정과 프로필은 삭제됐고 공유 콘텐츠는 익명으로 보존됐습니다. 브라우저 제한으로 일부 로컬 키를 지우지 못했습니다."
        : "계정과 프로필은 삭제됐고, 여행 기록·공유 일정·후기는 개인 식별 정보 없이 보존되었습니다.",
    }));
    return true;
  }, [
    anonymizeOwnedDocuments,
    archiveOwnedTrips,
    clearLocalPersonalData,
    deleteOwnedDocuments,
    setOverseasTransferAgreed,
    setPersonal,
    setPrivacyAgreed,
    skipDeletionSteps,
    updateDeletionStep,
  ]);

  const beginGoogleDeletionRedirect = useCallback(async (user, googleProvider) => {
    writeDeletionReauthRedirectMarker(user.uid);
    setAccountDeletion({
      ...createDeletionState(),
      status: "reauthenticating",
      provider: "google",
      uid: user.uid,
      message: "Google 본인 확인 페이지로 이동합니다. 확인 후 돌아오면 탈퇴가 자동으로 이어집니다.",
    });
    try {
      await reauthenticateWithRedirect(user, googleProvider);
    } catch (error) {
      clearDeletionReauthRedirectMarker();
      throw error;
    }
  }, []);

  const deleteAccount = useCallback(async (password = "") => {
    const user = fbAuth.currentUser;
    if (!user) {
      setAccountDeletion(current => ({
        ...current,
        status: "error",
        message: "로그인 정보를 찾을 수 없습니다. 다시 로그인한 뒤 탈퇴를 시도해주세요.",
      }));
      return false;
    }

    const provider = getDeletionProvider(user);
    setAccountDeletion({
      ...createDeletionState(),
      status: "reauthenticating",
      provider,
      uid: user.uid,
      message: provider === "google"
        ? "Google 계정으로 본인 확인 중..."
        : "비밀번호로 본인 확인 중...",
    });

    try {
      if (provider === "google") {
        const googleProvider = new GoogleAuthProvider();
        googleProvider.setCustomParameters({ prompt: "select_account" });
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (isMobile) {
          await beginGoogleDeletionRedirect(user, googleProvider);
          return false;
        }

        try {
          writeDeletionReauthRedirectMarker(user.uid);
          await reauthenticateWithPopup(user, googleProvider);
          clearDeletionReauthRedirectMarker();
        } catch (popupError) {
          const popupCode = popupError?.code || "";
          const shouldUseRedirect = [
            "auth/popup-blocked",
            "auth/cancelled-popup-request",
            "auth/operation-not-supported-in-this-environment",
          ].includes(popupCode);
          if (!shouldUseRedirect) {
            clearDeletionReauthRedirectMarker();
            throw popupError;
          }
          await beginGoogleDeletionRedirect(user, googleProvider);
          return false;
        }
      } else {
        if (!password) {
          throw profileError("auth/password-required", "현재 비밀번호를 입력해주세요.");
        }
        const credential = EmailAuthProvider.credential(user.email || "", password);
        await reauthenticateWithCredential(user, credential);
      }
    } catch (error) {
      const code = error?.code || "";
      let message = "본인 확인에 실패했습니다. 다시 시도해주세요.";
      if (code === "auth/password-required") message = error.message;
      else if (code === "auth/wrong-password" || code === "auth/invalid-credential") message = "비밀번호가 올바르지 않습니다.";
      else if (code === "auth/popup-closed-by-user" || code === "auth/redirect-cancelled-by-user") message = "Google 본인 확인이 취소되었습니다.";
      else if (code === "auth/user-mismatch") message = "현재 로그인한 계정과 같은 Google 계정을 선택해주세요.";
      setAccountDeletion(current => ({
        ...current,
        status: "requiresReauth",
        message,
      }));
      return false;
    }

    return runAccountDeletion(user, provider);
  }, [beginGoogleDeletionRedirect, runAccountDeletion]);

  // Google 로그인 redirect와 회원 탈퇴 재인증 redirect의 복귀 결과를 한 곳에서 구분해 처리한다.
  useEffect(() => {
    let active = true;
    const pendingDeletionUid = readDeletionReauthRedirectMarker();

    getRedirectResult(fbAuth)
      .then(async (result) => {
        if (!active) return;
        if (!pendingDeletionUid) {
          if (result?.user) await handleGoogleAuthResult(result.user);
          return;
        }

        clearDeletionReauthRedirectMarker();
        // 여기서부터는 탈퇴 흐름의 복귀다. 어느 갈래로 가든 화면을 내 정보의 탈퇴 패널로 데려가야 한다.
        // 그러지 않으면 되돌릴 수 없는 삭제가 인트로 화면 뒤에서 아무 표시 없이 돈다.
        setDeletionResumedFromRedirect(true);
        if (!result?.user) {
          setAccountDeletion({
            ...createDeletionState(),
            status: "requiresReauth",
            provider: "google",
            uid: pendingDeletionUid,
            message: "Google 본인 확인 결과를 확인하지 못했습니다. 회원 탈퇴를 다시 시도해주세요.",
          });
          return;
        }
        if (result.user.uid !== pendingDeletionUid) {
          setAccountDeletion({
            ...createDeletionState(),
            status: "requiresReauth",
            provider: "google",
            uid: pendingDeletionUid,
            message: "현재 로그인한 계정과 같은 Google 계정으로 다시 본인 확인해주세요.",
          });
          return;
        }
        await runAccountDeletion(result.user, "google");
      })
      .catch((error) => {
        if (!active) return;
        if (pendingDeletionUid) {
          clearDeletionReauthRedirectMarker();
          setDeletionResumedFromRedirect(true);
          setAccountDeletion({
            ...createDeletionState(),
            status: "requiresReauth",
            provider: "google",
            uid: pendingDeletionUid,
            message: error?.code === "auth/user-mismatch"
              ? "현재 로그인한 계정과 같은 Google 계정으로 다시 본인 확인해주세요."
              : "Google 본인 확인 복귀 처리에 실패했습니다. 회원 탈퇴를 다시 시도해주세요.",
          });
          return;
        }
        if (error.code) showGoogleError(error);
      })
      .finally(() => {
        if (active) setGoogleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [handleGoogleAuthResult, runAccountDeletion, showGoogleError]);

  const resetAccountDeletion = useCallback(() => {
    if (accountDeletion.status === "deleting" || accountDeletion.status === "reauthenticating") return;
    setAccountDeletion(createDeletionState());
  }, [accountDeletion.status]);

  // ── [Firebase] Auth: 로그아웃 ──
  const logout = async () => {
    ++profileRequestRef.current;
    try { await signOut(fbAuth); } catch (e) {}
    setLoggedInUser(null);
    setFirebaseUid(null);
    setAuthMode(null);
    setLoginEmail("");
    setLoginPassword("");
    setSignupPassword("");
    setSignupPasswordConfirm("");
    setNameCheckStatus("idle");
    setNameCheckMsg("");
    persistedPrivacyAgreedRef.current = false;
    persistedOverseasTransferAgreedRef.current = false;
    setPrivacyAgreed(false);
    setOverseasTransferAgreed(false);
    closeConsentGate();
    setProfileRequired(false);
    setProfileError("");
    setProfileLoadError("");
    setPersonal({ ...EMPTY_PERSONAL });
  };

  // ── [Firebase] 프로필 저장 / 신규 Google·고아 계정 복구 ──
  const saveProfileToFirestore = async () => {
    const uid = firebaseUid || fbAuth.currentUser?.uid;
    const email = fbAuth.currentUser?.email || loggedInUser || personal?.email || "";

    setProfileSaved(false);
    setProfileError("");
    if (!uid) {
      setProfileError("프로필을 저장하려면 먼저 로그인해주세요.");
      return false;
    }

    setProfileSaving(true);
    try {
      await persistProfile({ uid, email, profileData: personal, isNewAccount: profileRequired });
      persistedPrivacyAgreedRef.current = true;
      persistedOverseasTransferAgreedRef.current = true;
      setPrivacyAgreed(true);
      setOverseasTransferAgreed(true);
      setProfileRequired(false);
      setAuthMode(current => current === "profile" ? null : current);
      setNameCheckStatus("ok");
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
      return true;
    } catch (error) {
      console.error("프로필 저장 오류:", error);
      if (error?.code === "profile/username-taken") {
        setNameCheckStatus("fail");
        setNameCheckMsg("이미 사용 중인 닉네임입니다. 다른 닉네임을 선택해주세요.");
        setProfileError("이미 사용 중인 닉네임입니다.");
      } else if (error?.code === "profile/consents-required") {
        setProfileError("개인정보 수집·이용 및 국외 이전에 모두 동의해주세요.");
      } else if (error?.code === "profile/username-too-long") {
        setProfileError(`닉네임은 ${MAX_USERNAME_LENGTH}자 이하로 입력해주세요.`);
      } else if (error?.code === "profile/invalid-username") {
        setProfileError("닉네임 형식이 올바르지 않습니다.");
      } else {
        setProfileError("프로필 저장에 실패했습니다. 네트워크 연결을 확인한 뒤 다시 시도해주세요.");
      }
      return false;
    } finally {
      setProfileSaving(false);
    }
  };

  return {
    authMode, setAuthMode,
    loggedInUser, firebaseUid,
    loginEmail, setLoginEmail,
    loginPassword, setLoginPassword,
    loginError, setLoginError,
    signupPassword, setSignupPassword,
    signupPasswordConfirm, setSignupPasswordConfirm,
    showLoginPw, setShowLoginPw,
    showSignupPw, setShowSignupPw,
    rememberPw, setRememberPw,
    googleLoading,
    nameCheckStatus, setNameCheckStatus, nameCheckMsg, setNameCheckMsg,
    checkNameDuplicate,
    privacyAgreed, setPrivacyAgreed, privacyExpanded, setPrivacyExpanded,
    overseasTransferAgreed, setOverseasTransferAgreed,
    overseasTransferExpanded, setOverseasTransferExpanded,
    consentGate, hasRequiredConsents, requestRequiredConsent,
    confirmConsentGate, closeConsentGate,
    accountDeletion,
    accountDeletionProvider: accountDeletion.provider || getDeletionProvider(fbAuth.currentUser),
    deleteAccount, resetAccountDeletion,
    deletionResumedFromRedirect,
    profileRequired,
    profileSaving, profileSaved, profileError: profileErrorMessage, setProfileError,
    profileLoading, profileLoadError, setProfileLoadError,
    loginUser,
    logout,
    googleSignIn,
    saveUserAccount,
    saveProfileToFirestore,
    handleGoogleAuthResult,
  };
}
